// The No owner list on the Leads page (dashboard_unowned_leads, dashboard_handle_unowned_lead): leads no client owned,
// given to a client (sent to them as a new lead, or not), sent elsewhere (to whom, for how much) or marked not ours.
import {money} from './business-kpis-model';

export type LeadType='legitimate'|'needs_review'|'no_details'|'not_a_lead'|'solicitation'|'spam';
export type Reason='spam'|'out_of_area'|'not_our_service'|'duplicate'|'other';
export type Show='open'|'handled';
export type Kinds='real'|'all';
export type Days=7|30|90;
export type Suggestion={business_id:string;client:string;area:string|null;miles_outside:number;same_line:boolean};
/** What giving a lead queued for the client, and why nothing went when nothing did. */
export type Queued={texts:number;emails:number;manager_copies:number;held:string|null;exception?:string|null;paused?:boolean;
 waiting_for_classification?:boolean;texts_off?:boolean;emails_off?:boolean};
export type Decision={action:'given'|'elsewhere'|'not_ours';business_id:string|null;client:string|null;sent:boolean|null;queued:Queued|null;
 recipient:string|null;price:number|string|null;reason:Reason|null;note:string|null;decided_at:string;decided_by:string|null};
export type UnownedLead={lead_id:number;received_at:string;website_id:number;site:string|null;line_key:string;channel:'call'|'form';lead_type:LeadType;
 name:string|null;phone:string|null;email:string|null;city:string|null;state:string|null;zip:string|null;area:string|null;service:string|null;
 message:string|null;suggestions:Suggestion[];decision:Decision|null};
export type UnownedClient={business_id:string;name:string;lines:string[];areas:string[]};
export type UnownedCounts={open:number;open_real:number;handled:number;given:number;elsewhere:number;elsewhere_value:number|string;not_ours:number};
export type UnownedList={days:Days;show:Show;kinds:Kinds;line:string|null;generated_at:string;counts:UnownedCounts;total:number;
 lines:{line_key:string;label:string}[];clients:UnownedClient[];leads:UnownedLead[]};
export type Mode='give'|'elsewhere'|'not_ours';
export type HandleResult={lead_id:number;action:'given'|'elsewhere'|'not_ours'|'reopened';client?:string;sent?:boolean;queued?:Queued;recipient?:string;price?:number|string|null;reason?:Reason};

export const typeLabels:Record<LeadType,string>={legitimate:'Lead',needs_review:'To sort',no_details:'No details',not_a_lead:'Not a lead',solicitation:'Sales pitch',spam:'Spam'};
export const reasonLabels:Record<Reason,string>={spam:'Spam or a sales call',out_of_area:'Outside every area we serve',not_our_service:'A service we don’t offer',
 duplicate:'Already have it (a duplicate)',other:'Something else'};
/** Why a lead given to a client was not sent to them. */
const heldLabels:Record<string,string>={
 handed_over_quietly:'nothing was sent, as chosen',
 no_caller_speech:'nobody spoke on the call, so there was nothing to send',
 client_channel_or_territory_restricted:'not sent: the client’s settings hold back leads like this (their territory, websites or channel)',
 county_verification_required:'not sent: the client takes leads only in their counties, and this lead’s county is not known',
 client_email_missing:'not sent: the client has no email on file',
 client_mobile_missing:'not sent: the client has no mobile on file',
 no_contact:'not sent: the client has no mobile or email on file',
 sending_off:'not sent: client texts and emails are switched off',
 owner_not_resolved:'not sent: an earlier routing decision holds this lead',
 drainscape_route_no_longer_eligible:'not sent: the Drainscapes route for this lead no longer applies',
};
const plural=(n:number,one:string,many=`${one}s`)=>`${n} ${n===1?one:many}`;
export const heldLabel=(code:string)=>heldLabels[code]??`not sent (${code.replace(/_/g,' ')})`;

/** What happened when a lead was given: queued for the client, or why not. */
export function queuedText(q:Queued|null|undefined):string{
 if(!q)return '';
 if(q.held)return heldLabel(q.held);
 const parts=[q.texts?plural(q.texts,'text'):'',q.emails?plural(q.emails,'email'):''].filter(Boolean);
 let text=`${parts.join(' and ')} on the way`;
 if(q.manager_copies)text+=`, with ${plural(q.manager_copies,'copy','copies')} for their account manager`;
 if(q.paused)text+=' once sending resumes (all sending is paused)';
 else if(q.waiting_for_classification)text+=' once the lead is sorted';
 if(q.exception==='client_mobile_missing')text+='; no text: the client has no mobile on file';
 if(q.exception==='client_email_missing')text+='; no email: the client has no email on file';
 return text;
}

/** One line for a decision: who got the lead, or why it is not ours. */
export function decisionText(d:Decision|HandleResult,client?:string|null):string{
 if(d.action==='given'){
  const who=client??('client' in d?d.client:null)??'the client';
  return d.sent?`Given to ${who}: ${queuedText(d.queued)}.`:`Given to ${who} without sending.`;
 }
 if(d.action==='elsewhere'){
  const price=d.price===null||d.price===undefined||d.price===''?'':` for ${money(Number(d.price),true)}`;
  return `Sent elsewhere: ${d.recipient}${price}.`;
 }
 if(d.action==='not_ours')return `Not ours: ${d.reason?reasonLabels[d.reason].toLowerCase():'no reason given'}.`;
 return 'Back on the open list.';
}

/** The clients to give a lead to: the suggested ones first, in their order, then every other client by name. */
export function clientChoices(clients:UnownedClient[],lead:UnownedLead){
 const suggested=lead.suggestions.filter(s=>clients.some(c=>c.business_id===s.business_id));
 const taken=new Set(suggested.map(s=>s.business_id));
 return {suggested,others:clients.filter(c=>!taken.has(c.business_id)).sort((a,b)=>a.name.localeCompare(b.name))};
}

export const suggestionHint=(s:Suggestion)=>[s.miles_outside>0?`${s.miles_outside} mi outside their area`:'inside their area',s.same_line?'same line':'another line'].join(' · ');

type Request={ok:true;request:Record<string,unknown>}|{ok:false;error:string};
const withNote=(request:Record<string,unknown>,note:string)=>note.trim()?{...request,note:note.trim()}:request;

export function giveRequest(lead:UnownedLead,businessId:string,send:boolean,note:string):Request{
 if(!businessId)return {ok:false,error:'Choose the client to give it to.'};
 if(note.trim().length>1000)return {ok:false,error:'Keep the note to 1,000 characters.'};
 return {ok:true,request:withNote({action:'give',lead_id:lead.lead_id,business_id:businessId,send},note)};
}

/** A price in dollars: blank, or an amount ("$150", "1,200.50"). */
export function readPrice(text:string):{ok:true;value:string|null}|{ok:false}{
 const t=text.trim().replace(/^\$/,'').replace(/,/g,'');
 if(!t)return {ok:true,value:null};
 return /^\d{1,7}(\.\d{1,2})?$/.test(t)?{ok:true,value:t}:{ok:false};
}

export function elsewhereRequest(lead:UnownedLead,recipient:string,price:string,note:string):Request{
 const who=recipient.trim();
 if(!who)return {ok:false,error:'Say who got the lead.'};
 if(who.length>200)return {ok:false,error:'Keep who got it to 200 characters.'};
 const p=readPrice(price);
 if(!p.ok)return {ok:false,error:'Enter the price as an amount in dollars, like 150 or 150.50, or leave it blank.'};
 if(note.trim().length>1000)return {ok:false,error:'Keep the note to 1,000 characters.'};
 return {ok:true,request:withNote({action:'elsewhere',lead_id:lead.lead_id,recipient:who,price:p.value},note)};
}

export function notOursRequest(lead:UnownedLead,reason:Reason|'',note:string):Request{
 if(!reason)return {ok:false,error:'Choose why it is not ours.'};
 if(note.trim().length>1000)return {ok:false,error:'Keep the note to 1,000 characters.'};
 return {ok:true,request:withNote({action:'not_ours',lead_id:lead.lead_id,reason},note)};
}

export const leadName=(l:UnownedLead)=>l.name??(l.channel==='call'?'Caller':'No name');
